<template>
  <header
    v-if="router.path !== '/checkout'"
    class="bg-white sm:z-40 md:z-50 z-30 sticky w-full -top-1 transition-all"
    :class="{ 'nav:-top-5': !showTopBar }"
    id="header"
  >
    <NuxtLayout name="stretch" class="topBar hidden nav:block pt-1" :class="{ 'nav:is-hidden': !showTopBar }">
      <div class="flex flex-wrap items-center mx-auto justify-end">
        <div class="flex md:order-2">
          <CommonCallNumber headerClass="text-[13px]" callClass="text-secondary font-bold" />
        </div>
      </div>
    </NuxtLayout>

    <div
      class="fixed nav:hidden top-0 w-full bg-grey-light z-[555] ease-in-out duration-200"
      :class="isOpen ? 'translate-x-0 ' : 'translate-x-full  '"
    >
      <div class="bg-white">
        <div class="py-4 px-3 justify-center flex border-b">
          <NuxtLink :to="localeHomePath(switchBlogLocale())" class="flex items-center">
            <NuxtImg loading="lazy" src="/img/logo.svg" class="w-44" alt="Better2Know" width="250" height="100" />
          </NuxtLink>

          <button class="">
            <XIcon @click="close" class="w-7 h-7 absolute top-[18px] right-4 text-black" />
          </button>
        </div>

        <!-- MAIN MENU  -->
        <MenuMobile v-if="isOpen" :allSites="allSites" :close-menu="close" :isOpen="isOpen" class="h-screen z-[4]" />
      </div>
    </div>

    <nav class="w-full p-4 nav:py-0 xl:p-0 mx-auto md:max-w-7xl justify-center">
      <div class="flex flex-wrap justify-between">
        <NuxtLink :to="localeHomePath(switchBlogLocale())" class="flex items-center">
          <NuxtImg
            loading="lazy"
            src="/img/logo.svg"
            class="w-44 md:w-52 nav:w-52 xl:w-60 max-w-full"
            alt="Better2Know"
            width="250"
            height="100"
          />
        </NuxtLink>

        <div class="flex items-center md:order-1 space-x-6">
          <!--<button class="md:hidden">
            <NuxtImg src="/icons/search.svg" alt="search" />
          </button>
          <button class="md:hidden">
            <NuxtImg src="/icons/shopping-cart.svg" alt="cart" />
          </button>
          -->

          <!-- Mobile account drawer trigger -->
          <AccountTrigger class="md:hidden" iconClass="h-5" />

          <CartMenu
            v-if="isHomeTestEnabled"
            iconClass="h-5"
            class="md:hidden"
            @clicked="cartStore.toggleCartDrawer()"
            variant="dark"
          />

          <button @click="showMenu" class="nav:hidden">
            <NuxtImg loading="lazy" src="/icons/menu-icon.svg" alt="" class="w-9 h-9" width="32" height="32" />
          </button>
        </div>

        <div class="hidden ml-auto nav:flex md:w-auto md:order-2 mx-4" id="mobile-menu-3">
          <ul class="flex flex-col md:flex-row gap-x-12 text-base xl:text-lg font-semibold">
            <LayoutMegaMenuAnchor to="/get-started">
              {{ $t("mainmenu.getStarted") }}
            </LayoutMegaMenuAnchor>
            <LayoutMegaMenuAnchor to="/clinics">
              {{ $t("mainmenu.clinics") }}
            </LayoutMegaMenuAnchor>
            <LayoutMegaMenuAnchor to="/tests" anchor-key="tests">
              {{ $t("mainmenu.tests") }}
            </LayoutMegaMenuAnchor>

            <LayoutMegaMenuAnchor to="/tests/home-tests" v-if="isHomeTestEnabled">
              {{ $t("mainmenu.homeTest") }}
            </LayoutMegaMenuAnchor>

            <LayoutMegaMenuAnchor to="/help-and-advice" anchor-key="help-and-advice">
              {{ $t("mainmenu.helpAndAdvice") }}
            </LayoutMegaMenuAnchor>
          </ul>
        </div>

        <div
          class="hidden items-center w-full min-h-[56px] nav:flex md:w-auto md:space-x-8 m-4 md:order-2"
          id="mobile-menu-2"
        >
          <AccountTrigger inject-input />
          <CartMenu v-if="isHomeTestEnabled" @clicked="cartStore.toggleCartDrawer" :variant="`dark`" />

          <!--<button
            type="button"
            data-collapse-toggle="mobile-menu-2"
            aria-controls="mobile-menu-2"
            aria-expanded="false"
            class="text-gray-500 focus:outline-none focus:ring-4 focus:ring-gray-200 rounded-lg text-sm"
          >
            <NuxtImg src="/icons/search.svg" alt="" />
          </button>
          <button>
            <NuxtImg src="/icons/shopping-cart.svg" alt="" />
          </button>-->
          <Menu :allSites="allSites" class="z-[4]" />
        </div>
      </div>
    </nav>
    <div class="w-full bg-gray-200 h-0.5">
      <CommonProgressBar v-if="commonBar.includes(getLocaleUrl)" />
    </div>

    <!-- Adding Mega menu to the layout -->
    <LayoutMegaMenu />

    <LazyCartDetailDrawer
      hydrate-on-visible
      :open="cartStore.isCartDrawerOpen"
      @closeModal="cartStore.closeCartDrawer()"
    />

    <AccountDrawer />
    <CountrySwitcher :allSites="allSites" class="z-[4]" />
  </header>
</template>

<script setup>
import { useGlobalStore } from "@/stores/global";
import { XIcon } from "@heroicons/vue/solid";

const globalStore = useGlobalStore();
const router = useRoute();
const getLocaleUrl = computed(() => {
  const splitUrl = router.name?.split("___");
  return splitUrl?.length > 0 ? splitUrl[0] : router.name;
});

const isOpen = ref(false);
const showTopBar = ref(true);
const lastScrollPosition = ref(0);
const scrollOffset = ref();
const allSites = ref({});
allSites.value = globalStore.siteAllLocales;

const cartStore = useCartStore();
const isCartDrawerOpen = computed(() => cartStore.isCartDrawerOpen);
const isHomeTestEnabled = computed(() => globalStore.isHomeTestEnabled);

onMounted(() => {
  lastScrollPosition.value = window.pageYOffset;
  window.addEventListener("scroll", onScroll);
});

onBeforeUnmount(() => {
  window.removeEventListener("scroll", onScroll);
});

const showMenu = () => {
  document.body.classList.toggle("overflow-hidden");
  isOpen.value = true;
};
const close = () => {
  document.body.classList.toggle("overflow-hidden");
  isOpen.value = false;
};

const onScroll = () => {
  let bottomOfWindow =
    Math.max(window.pageYOffset, document.documentElement.scrollTop, document.body.scrollTop) + window.innerHeight ===
    document.documentElement.offsetHeight;

  if (window.pageYOffset < 150) {
    return;
  }
  if (Math.abs(window.pageYOffset - lastScrollPosition.value) < scrollOffset.value) {
    return;
  }

  showTopBar.value = window.pageYOffset < lastScrollPosition.value || bottomOfWindow;
  lastScrollPosition.value = window.pageYOffset;
};

const commonBar = ["blog-slug", "help-and-advice-category-slug"];

await globalStore.resetHomeTestState().catch((err) => {
  console.error("[DefaultHeader] resetHomeTestState failed:", err);
});
</script>

<!-- Other scoped styles -->
<style scoped>
.topBar {
  transform: translateY(0);
  transition: transform 400ms ease;
}

.topBar.is-hidden {
  transform: translateY(-100%);
}
</style>
